import React from 'react';
import type { CampaignResponse } from '@troia-v3/types';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';

/**
 * Linha de tabela de campanha (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + colunas (nome, canal, status, enviados, taxa, data)
 * + menu ••• (última col) com ações de ciclo de vida status-aware.
 */
export interface CampaignListItemProps {
  campaign: CampaignResponse;
  channelName?: string;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  onActivate?: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
}

const formatDate = (date?: Date | string): string => {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const CampaignListItem: React.FC<CampaignListItemProps> = ({
  campaign,
  channelName,
  selected = false,
  onToggleSelect,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
  onActivate,
  onPause,
  onResume,
  onCancel,
}) => {
  const stats = campaign.stats;
  const totalSent = stats?.messagesSent ?? 0;
  const totalRecipients = stats?.totalRecipients || campaign.recipientIds?.length || 0;
  const successRate = totalSent > 0 ? ((stats?.messagesDelivered ?? 0) / totalSent) * 100 : null;
  const dateValue = campaign.scheduledFor ?? stats?.startedAt ?? campaign.createdAt;

  const { status } = campaign;

  const actions: CardAction[] = [];
  if (status === 'draft' && onActivate) actions.push({ label: 'Ativar', icon: 'play', onClick: () => onActivate(campaign.id) });
  if ((status === 'scheduled' || status === 'in_progress') && onPause) actions.push({ label: 'Pausar', icon: 'pause', onClick: () => onPause(campaign.id) });
  if (status === 'paused' && onResume) actions.push({ label: 'Retomar', icon: 'play', onClick: () => onResume(campaign.id) });
  if ((status === 'scheduled' || status === 'in_progress' || status === 'paused') && onCancel) actions.push({ label: 'Cancelar', icon: 'square', onClick: () => onCancel(campaign.id) });
  if (onView) actions.push({ label: 'Ver relatório', icon: 'eye', onClick: () => onView(campaign.id) });
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(campaign.id) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(campaign.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(campaign.id), variant: 'danger' });

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onClick={() => onView?.(campaign.id)}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`campaign-row-${campaign.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onToggleSelect?.(campaign.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`campaign-check-${campaign.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Campanha */}
      <td className="px-4 py-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {campaign.name}
          </div>
          <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {totalRecipients.toLocaleString()} destinatários
          </div>
        </div>
      </td>

      {/* Canal */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold max-w-[180px]"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          <Icon name="send" className="w-3 h-3 flex-none" />
          <span className="truncate">{channelName ?? campaign.channelId}</span>
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <CampaignStatusBadge status={campaign.status} autoPause={campaign.autoPause} data-testid={`campaign-status-${campaign.id}`} />
      </td>

      {/* Enviados */}
      <td className="px-4 py-3 text-sm tabular-nums whitespace-nowrap" style={{ color: 'var(--theme-card-text)' }}>
        {totalSent.toLocaleString()} / {totalRecipients.toLocaleString()}
      </td>

      {/* Taxa de Sucesso */}
      <td className="px-4 py-3 text-sm font-semibold tabular-nums" style={{ color: successRate === null ? 'var(--theme-muted-text)' : 'var(--theme-card-text)' }}>
        {successRate === null ? '—' : `${successRate.toFixed(1)}%`}
      </td>

      {/* Data */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
        {formatDate(dateValue)}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        {actions.length > 0 && <CardActionsMenu actions={actions} size="sm" data-testid={`campaign-actions-${campaign.id}`} />}
      </td>
    </tr>
  );
};

CampaignListItem.displayName = 'CampaignListItem';
